import * as THREE from 'three';

/**
 * GradientSkybox - Creates a skybox with gradient color transition
 * This implementation uses a large sphere with a shader material to create a smooth gradient effect
 */
class GradientSkybox {
  /**
   * Constructor for gradient skybox
   * @param {Object} options - Options for the skybox
   * @param {Array} options.topColor - RGB array for top color [r, g, b], values 0-1
   * @param {Array} options.bottomColor - RGB array for bottom color [r, g, b], values 0-1
   * @param {number} options.offset - Offset value controlling the gradient midpoint position (-1 to 1)
   * @param {number} options.exponent - Exponent value controlling the gradient blending (higher = sharper transition)
   * @param {number} options.radius - Radius of the skybox sphere (default: 500)
   */
  constructor(options = {}) {
    this.options = {
      topColor: [0.38, 0.68, 0.9], // Default sky blue
      bottomColor: [0.95, 0.95, 0.95], // Default light white/gray
      offset: 0, // Gradient midpoint position (0 = middle, range: -1 to 1)
      exponent: 0.6, // Gradient smoothness (higher = sharper transition)
      radius: 500, // Sphere radius
      ...options
    };
    
    this.mesh = null;
  }
  
  /**
   * Create the skybox and add it to the scene
   * @param {THREE.Scene} scene - Scene to add skybox to
   * @returns {THREE.Mesh} The skybox mesh
   */
  create(scene) {
    if (!scene) {
      console.error('Scene is required to create skybox');
      return null;
    }
    
    // Create a sphere geometry
    // Invert so that the material renders on the inside
    const geometry = new THREE.SphereGeometry(this.options.radius, 32, 32);
    
    // Create the shader material with vertex and fragment shaders for gradient
    const material = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(...this.options.topColor) },
        bottomColor: { value: new THREE.Color(...this.options.bottomColor) },
        offset: { value: this.options.offset },
        exponent: { value: this.options.exponent }
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        
        varying vec3 vWorldPosition;
        
        void main() {
          // Get normalized direction vector from world center to fragment
          vec3 viewDirection = normalize(vWorldPosition - cameraPosition);
          
          // Calculate gradient mixing factor based on Y component
          // Higher values will be closer to top color
          float h = normalize(viewDirection).y;
          
          // Apply offset and exponent to control gradient transition
          float mixFactor = max(0.0, min(1.0, pow(max(0.0, h + offset), exponent)));
          
          // Mix colors based on the factor
          gl_FragColor = vec4(mix(bottomColor, topColor, mixFactor), 1.0);
        }
      `,
      side: THREE.BackSide // Render on inside of sphere
    });
    
    // Create and add the skybox mesh
    this.mesh = new THREE.Mesh(geometry, material);
    
    // Make sure skybox doesn't interfere with other objects
    this.mesh.renderOrder = -1000;
    this.mesh.userData.isSkybox = true;
    
    scene.add(this.mesh);
    console.log('Gradient skybox created and added to scene');
    
    return this.mesh;
  }
  
  /**
   * Update skybox colors
   * @param {Array} topColor - RGB array for top color [r, g, b], values 0-1
   * @param {Array} bottomColor - RGB array for bottom color [r, g, b], values 0-1
   */
  updateColors(topColor, bottomColor) {
    if (!this.mesh) {
      console.warn('Skybox not created, cannot update colors');
      return;
    }
    
    const material = this.mesh.material;
    
    if (topColor) {
      material.uniforms.topColor.value = new THREE.Color(...topColor);
      this.options.topColor = topColor;
    }
    
    if (bottomColor) {
      material.uniforms.bottomColor.value = new THREE.Color(...bottomColor);
      this.options.bottomColor = bottomColor;
    }
  }
  
  /**
   * Update skybox gradient parameters
   * @param {number} offset - Offset value controlling the gradient midpoint position
   * @param {number} exponent - Exponent value controlling the gradient blending
   */
  updateGradient(offset, exponent) {
    if (!this.mesh) {
      console.warn('Skybox not created, cannot update gradient');
      return;
    }
    
    const material = this.mesh.material;
    
    if (offset !== undefined) {
      material.uniforms.offset.value = offset;
      this.options.offset = offset;
    }
    
    if (exponent !== undefined) {
      material.uniforms.exponent.value = exponent;
      this.options.exponent = exponent;
    }
  }
  
  /**
   * Dispose of skybox resources
   */
  dispose() {
    if (this.mesh) {
      if (this.mesh.parent) {
        this.mesh.parent.remove(this.mesh);
      }
      
      if (this.mesh.geometry) {
        this.mesh.geometry.dispose();
      }
      
      if (this.mesh.material) {
        this.mesh.material.dispose();
      }
      
      this.mesh = null;
      console.log('Skybox disposed');
    }
  }
}

export default GradientSkybox; 